<template>
  <!-- The new look: every deadline as a tile, a row per year — done green,
       missed amber, the next one blue. Kustuta stays on the ones with no
       hooldus against them, as in the table. -->
  <div v-if="plate" class="obj-card">
    <div class="obj-card__head">
      <h2>Hoolduse tähtajad</h2>
      <span class="obj-legend">
        <span><i style="background: #2E7D32" />tehtud</span>
        <span><i style="background: #8F5400" />hiljaks</span>
        <span><i style="background: #1F5FD1" />järgmine</span>
      </span>
    </div>
    <div v-if="!loading && !years.length" class="obj-empty">Hoolduse tähtaegu pole.</div>
    <div v-else class="obj-years">
      <div v-for="year in years" :key="year.year" class="obj-year">
        <b class="obj-wide">{{ year.year }}</b>
        <div class="obj-tiles" :class="{ 'obj-tiles--monthly': year.monthly }">
          <div v-for="tile in year.tiles" :key="tile.id" class="obj-tile" :class="'obj-tile--' + tile.state">
            <span class="obj-cap">{{ tile.label }}</span>
            <b>{{ tile.title }}</b>
            <span>{{ tile.sub }}</span>
            <button v-if="tile.removable" type="button" class="obj-link obj-link--danger" @click="confirm = tile.interval">Kustuta tähtaeg</button>
          </div>
        </div>
      </div>
    </div>
    <confirm @cancel="confirm = null" @confirm="remove(confirm)" :visible="!!confirm"
      heading="Kustuta hoolduse tähtaeg?" text="Tähtaeg kaob hoolduskavast. Juba tehtud hooldusi see ei puuduta." action="Kustuta tähtaeg"
      :detail="confirm ? confirm.title || '' : ''" />
  </div>
  <div v-else>
    <v-data-table-server
      :headers="headers"
      :items="intervals"
      :loading="loading"
      :items-length="count"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="itemsPerPageOptions"
    >
      <template v-slot:[`item.date`]="{item}">
         {{format(item.date)}}
      </template>
       <template v-slot:[`item.note.entryDate`]="{item}">
         {{item.note ? formatDate(item.note.entryDate) : '-'}}
      </template>
      <template v-slot:[`item.actions`]="{item}">
        <v-btn v-if="!item.note" @click="confirm = item" color="red" size="small">Kustuta</v-btn>
      </template>
    </v-data-table-server>
    <confirm @cancel="confirm = null" @confirm="remove(confirm)" :visible="!!confirm"
      heading="Kustuta hoolduse tähtaeg?" text="Tähtaeg kaob hoolduskavast. Juba tehtud hooldusi see ei puuduta." action="Kustuta tähtaeg"
      :detail="confirm ? confirm.title || '' : ''" />
  </div>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import { pickBy } from 'lodash'
import { capitalize } from '@/plugins/utils'
import listMixin from '@/plugins/listMixin'
import helpersMixin from '@/plugins/helpersMixin'

export default {
  mixins: [ listMixin, helpersMixin ],
  props: {
    object: {
      required: true
    },
    // The object page's new look (Seaded -> Objekt uues kujunduses).
    plate: {
      type: Boolean,
      default: false
    },
    overview: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      confirm: null,
      intervals: [],
      count: 0,
      page: 1,
      itemsPerPage: 15,
      loading: true,
      pagination: {},
      headers: [{
        title: 'Tähtaeg',
        align: 'start',
        value: 'date',
        sortable: false
      }, {
        title: 'Hooldus tehtud',
        align: 'start',
        value: 'note.entryDate',
        sortable: false
      }, {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end'
      }]
    }
  },
  watch: {
    params: {
      handler() {
        this.refresh()
      },
      deep: true
    }
  },
  computed: {
    // The tiles, a row per year. The first deadline still open from today on
    // is the next one; an open one before today is late.
    years () {
      const today = dayjs().startOf('day')
      const sorted = this.intervals.slice().sort((a, b) => dayjs(a.date) - dayjs(b.date))
      const next = sorted.find(i => !i.note && !dayjs(i.date).isBefore(today))
      const monthly = this.object.contractInterval === this.contractIntervals.MONTHLY
      const ROMAN = ['I', 'II', 'III', 'IV']
      const byYear = {}
      for (const interval of sorted) {
        const d = dayjs(interval.date)
        const month = capitalize(d.format('MMMM'))
        const state = interval.note ? 'done'
          : d.isBefore(today) ? 'late'
            : interval === next ? 'next' : 'future'
        const tile = {
          id: interval.id,
          interval,
          state,
          label: monthly ? month.toUpperCase() : ROMAN[Math.floor(d.month() / 3)] + ' KV · ' + month.toUpperCase(),
          title: state === 'done' ? 'Tehtud ' + this.formatDate(interval.note.entryDate)
            : state === 'late' ? 'Tegemata'
              : state === 'next' ? 'Järgmine' : 'Tulemas',
          sub: state === 'done' ? '' : 'tähtaeg ' + d.format('DD.MM.YYYY'),
          removable: !interval.note
        }
        ;(byYear[d.year()] = byYear[d.year()] || []).push(tile)
      }
      return Object.keys(byYear).sort().map(year => ({ year, monthly, tiles: byYear[year] }))
    },
    params () {
      if (this.plate) {
        // One page holds every deadline the tiles need.
        return { limit: 500, offset: 0 }
      }
      return pickBy({
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        ...this.search
      }, v => v !== undefined)
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      // Leaving the page clears the route's objectId before this tab is gone.
      if (!this.$route.params.objectId) {
        return
      }
      this.loading = true
      try {
        const { intervals, count } = await api.getObjectIntervals(this.$route.params.objectId, this.params)
        this.intervals = intervals
        this.count = count
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    format (date) {
      return capitalize(dayjs(date).format('MMMM YYYY'))
    },
    formatDate (date) {
      return capitalize(dayjs(date).format('DD.MM.YYYY'))
    },
    async remove (item) {
      this.confirm = null
      this.loading = true
      try {
        await api.deleteObjectInterval(item.id, this.object.id)
        this.loading = false
        this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
        this.loading = false
      }
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>